import { useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import * as ArchiveStatus from '../../../../shared/ArchiveStatus';
import { checkIfOrderIsArchived } from '../../OrdersActions';
import * as Actions from '../Actions';

export const viewOrderArchivedStatusQueryKey = 'view-order-archived-status';
export const viewOrderCommonQueryKey = 'view-order-common';

export type ViewOrderArchiveState = {
    photoIsArchived: boolean;
    planIsArchived: boolean;
    archivedStatus: number;
};

export type ViewOrderLoadFailure = 'archive-status-check' | 'order-load';

type UseViewOrderPageDataProps = {
    orderId: number;
    onArchiveStatusResolved: (archiveState: ViewOrderArchiveState) => void;
    onOrderLoaded: () => void;
};

type UseViewOrderPageDataResult = ViewOrderArchiveState & {
    loading: boolean;
    reloadOrder: () => void;
    loadFailure: ViewOrderLoadFailure | null;
    dismissLoadFailure: () => void;
};

const queryBehavior = {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: 0
};

const archiveStateWhileUnknown: ViewOrderArchiveState = {
    photoIsArchived: true,
    planIsArchived: true,
    archivedStatus: 0
};

export function useViewOrderPageData(props: UseViewOrderPageDataProps): UseViewOrderPageDataResult {
    const { orderId, onArchiveStatusResolved, onOrderLoaded } = props;
    const dispatch = useDispatch<any>();

    const [archiveState, setArchiveState] = useState<ViewOrderArchiveState | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadFailure, setLoadFailure] = useState<ViewOrderLoadFailure | null>(null);

    const [lastRenderedOrderId, setLastRenderedOrderId] = useState(orderId);
    if (orderId !== lastRenderedOrderId) {
        setLastRenderedOrderId(orderId);
        setLoading(true);
        setArchiveState(null);
        setLoadFailure(null);
    }

    const lastCheckedOrderId = useRef<number | null>(null);

    const archivedStatusQuery = useQuery(
        [viewOrderArchivedStatusQueryKey, orderId],
        () => {
            const orderChanged =
                lastCheckedOrderId.current !== null && lastCheckedOrderId.current !== orderId;
            lastCheckedOrderId.current = orderId;
            const resetIfOrderChanged = orderChanged
                ? dispatch(Actions.resetOrderStates(orderId))
                : Promise.resolve();
            return resetIfOrderChanged.then(() => dispatch(checkIfOrderIsArchived(orderId)));
        },
        {
            ...queryBehavior,
            onSuccess: (result: any) => {
                const photoIsArchived =
                    Boolean(result.photoIsArchived) &&
                    result.archivedStatus >= ArchiveStatus.ArchivedGlacier;
                const planIsArchived =
                    Boolean(result.planIsArchived) &&
                    result.archivedStatus >= ArchiveStatus.ArchivedGlacier;
                const resolvedArchiveState = {
                    photoIsArchived,
                    planIsArchived,
                    archivedStatus: result.archivedStatus
                };
                setArchiveState(resolvedArchiveState);
                onArchiveStatusResolved(resolvedArchiveState);
            },
            onError: () => setLoadFailure('archive-status-check')
        }
    );

    const archivedStatusCheckHasSettled =
        archivedStatusQuery.isSuccess || archivedStatusQuery.isError;

    const orderQuery = useQuery(
        [viewOrderCommonQueryKey, orderId],
        () => dispatch(Actions.get(orderId)),
        {
            ...queryBehavior,
            enabled: archivedStatusCheckHasSettled,
            onSuccess: () => {
                onOrderLoaded();
                setLoading(false);
            },
            onError: () => {
                setLoadFailure('order-load');
                setLoading(false);
            }
        }
    );

    const reloadOrder = (): void => {
        orderQuery.refetch();
    };

    const dismissLoadFailure = (): void => {
        setLoadFailure(null);
    };

    return {
        ...(archiveState !== null ? archiveState : archiveStateWhileUnknown),
        loading,
        reloadOrder,
        loadFailure,
        dismissLoadFailure
    };
}
